import React, { useEffect, useMemo, useRef, useState } from "react";
import { TrendingUp, Sparkles } from "lucide-react";

const SYMS = { USD:"$", EUR:"€", GBP:"£", NGN:"₦", ZAR:"R", KES:"KSh", GHS:"₵", EGP:"£", INR:"₹", BRL:"R$", MXN:"$", AED:"AED ", SAR:"SAR ", JPY:"¥", CNY:"¥", AUD:"A$", CAD:"C$", SGD:"S$" };

const fmtCurrency = (n, sym = "$", opts = {}) => {
  if (typeof n !== "number" || isNaN(n)) return `${sym}0`;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${sym}${(n / 1_000_000_000).toFixed(opts.decimals ?? 1)}B`;
  if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(opts.decimals ?? 1)}M`;
  if (abs >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}K`;
  return `${sym}${Math.round(n).toLocaleString()}`;
};

/**
 * Notion-style live ROI calculator for ABM pages.
 * Input changes are debounced (600ms) before firing `onEngage`.
 */
export default function ABMROICalculator({ page, onView, onEngage }) {
  const sym = page.currency_symbol || SYMS[(page.currency_code || "USD").toUpperCase()] || "$";
  const [revenue, setRevenue] = useState(page.roi_revenue_default ?? 500);
  const [leakPct, setLeakPct] = useState(page.roi_leak_default ?? 4);
  const recoveryPct = page.roi_recovery_pct ?? 60;
  const computed = useMemo(
    () => (revenue * 1_000_000) * (leakPct / 100) * (recoveryPct / 100),
    [revenue, leakPct, recoveryPct],
  );

  const viewedRef = useRef(false);
  const beatTimerRef = useRef(null);
  const lastBeatValRef = useRef("");

  useEffect(() => {
    if (!viewedRef.current) {
      viewedRef.current = true;
      onView?.();
    }
  }, [onView]);

  useEffect(() => {
    if (beatTimerRef.current) clearTimeout(beatTimerRef.current);
    beatTimerRef.current = setTimeout(() => {
      const sig = `${revenue}|${leakPct}`;
      if (sig === lastBeatValRef.current) return;
      lastBeatValRef.current = sig;
      onEngage?.({ revenue, leakPct, computed });
    }, 600);
    return () => { if (beatTimerRef.current) clearTimeout(beatTimerRef.current); };
  }, [revenue, leakPct, computed, onEngage]);

  return (
    <section
      data-testid="abm-roi-calculator"
      className="border border-zinc-900 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-6 md:p-8"
    >
      <div className="flex items-center gap-2 text-[#FFB000] font-mono text-[10px] uppercase tracking-widest mb-1">
        <Sparkles className="w-3 h-3" /> // INTERACTIVE — RUN THE NUMBERS YOURSELF
      </div>
      <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tighter mb-1">
        How much revenue is hiding in your data silos?
      </h2>
      <p className="text-sm text-zinc-400 mb-6">
        Plug in your annual revenue + an estimate of how much you think slips through. We&apos;ll show
        what DARA typically recovers in the first 12 months.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {page.roi_revenue_label || `Annual revenue (${sym}M)`}
          </label>
          <div className="font-heading font-black text-3xl text-white mt-1 mb-2">{sym}{revenue.toLocaleString()}M</div>
          <input
            type="range"
            min={page.roi_revenue_min ?? 10}
            max={page.roi_revenue_max ?? 10000}
            step={10}
            value={revenue}
            onChange={(e) => setRevenue(Number(e.target.value))}
            className="w-full accent-[#FFB000]"
            data-testid="abm-roi-revenue-slider"
          />
          <div className="flex justify-between font-mono text-[10px] text-zinc-600 mt-1">
            <span>{sym}{(page.roi_revenue_min ?? 10).toLocaleString()}M</span>
            <span>{sym}{(page.roi_revenue_max ?? 10000).toLocaleString()}M</span>
          </div>
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {page.roi_leak_label || "Estimated leak from data silos (%)"}
          </label>
          <div className="font-heading font-black text-3xl text-white mt-1 mb-2">{leakPct.toFixed(1)}%</div>
          <input
            type="range"
            min={page.roi_leak_min ?? 1}
            max={page.roi_leak_max ?? 10}
            step={0.1}
            value={leakPct}
            onChange={(e) => setLeakPct(Number(e.target.value))}
            className="w-full accent-[#FFB000]"
            data-testid="abm-roi-leak-slider"
          />
          <div className="flex justify-between font-mono text-[10px] text-zinc-600 mt-1">
            <span>{(page.roi_leak_min ?? 1).toFixed(1)}%</span>
            <span>{(page.roi_leak_max ?? 10).toFixed(1)}%</span>
          </div>
        </div>
      </div>

      <div className="mt-8 border-t border-zinc-900 pt-6">
        <div className="flex items-center gap-2 text-[#00E676] font-mono text-[10px] uppercase tracking-widest">
          <TrendingUp className="w-3 h-3" /> {page.roi_output_label || "What DARA recovers in 12 months"}
        </div>
        <div
          className="font-heading font-black text-5xl md:text-7xl tracking-tighter mt-2 text-[#FFB000] tabular-nums transition-colors"
          data-testid="abm-roi-output"
        >
          {fmtCurrency(computed, sym, { decimals: 1 })}
        </div>
        <div className="font-mono text-[11px] text-zinc-500 mt-2">
          Based on DARA recovering {recoveryPct}% of the revenue currently leaking from
          siloed data — a conservative figure for our enterprise pilots.
        </div>
      </div>
    </section>
  );
}
